import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from "react";
import axios from 'axios';
import config from '../components/config/api.config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  // Setup axios interceptor
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  const checkAuth = useCallback(() => {
    try {
      const storedToken = localStorage.getItem("token");
      const userData = localStorage.getItem("user");
      
      if (storedToken && userData) {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        setIsLoggedIn(true);
        setToken(storedToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      } else {
        setUser(null);
        setIsLoggedIn(false);
        setToken(null);
      }
    } catch (error) {
      console.error("Auth check error:", error);
      setUser(null);
      setIsLoggedIn(false);
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // ==================== LOGIN FUNCTION WITH SELLER VALIDATION ====================
  const login = useCallback(async (email, password) => {
    try {
      const response = await axios.post(`${config.apiUrl}${config.endpoints.login}`, {
        email,
        password
      });

      const { token, data } = response.data;
      const userData = data.user;

      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(userData));
      setToken(token);
      setUser(userData);
      setIsLoggedIn(true);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      return { success: true, user: userData };
    } catch (error) {
      console.error("Login error:", error);
      const errorData = error.response?.data;
      
      // ==================== SELLER VERIFICATION ERROR HANDLING ====================
      
      // Case 1: Store is PENDING (not yet verified by Super Admin)
      if (errorData?.code === 'STORE_PENDING') {
        return { 
          success: false, 
          message: errorData.message || 'Your store is pending approval. Please wait for admin verification.',
          code: 'STORE_PENDING'
        };
      }
      
      // Case 2: Store is REJECTED (rejected by Super Admin)
      if (errorData?.code === 'STORE_REJECTED') {
        return { 
          success: false, 
          message: errorData.message || 'Your store application has been rejected.',
          code: 'STORE_REJECTED',
          reason: errorData.reason
        };
      }
      
      // Case 3: Store is INACTIVE (other issues)
      if (errorData?.code === 'STORE_INACTIVE') {
        return { 
          success: false, 
          message: errorData.message || 'Your store account is not active. Please contact support.',
          code: 'STORE_INACTIVE'
        };
      }
      
      // Case 4: Other errors (invalid credentials, etc.)
      return {
        success: false,
        message: errorData?.message || 'Login failed. Please check your credentials.'
      };
    }
  }, []);

  const logout = useCallback(async () => {
    const userRole = user?.role; // Capture role before clearing
    
    try {
      await axios.post(`${config.apiUrl}${config.endpoints.logout}`);
    } catch (error) {
      console.error("Logout API error:", error);
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      setToken(null);
      setUser(null);
      setIsLoggedIn(false);
      delete axios.defaults.headers.common['Authorization'];
    }
    
    // Return the role so the component knows where to redirect
    return userRole;
  }, [user?.role]);

  // Get logout redirect path based on user role
  const getLogoutRedirectPath = useCallback(() => {
    if (user?.role === 'seller') return '/seller/login';
    if (user?.role === 'superadmin' || user?.role === 'admin') return '/admin/login';
    return '/login';
  }, [user?.role]);

  const updateUser = useCallback((userData) => {
    setUser(userData);
    localStorage.setItem("user", JSON.stringify(userData));
  }, []);

  const value = useMemo(() => {
    // Role check functions
    const isSuperAdmin = () => user?.role === 'superadmin';
    const isAdmin = () => user?.role === 'admin' || user?.role === 'superadmin';
    const isSeller = () => user?.role === 'seller';
    const isStoreVerified = () => user?.role === 'seller' && user?.store?.verificationStatus === 'verified';
    const isStorePending = () => user?.role === 'seller' && user?.store?.verificationStatus === 'pending';
    const isStoreRejected = () => user?.role === 'seller' && user?.store?.verificationStatus === 'rejected';
    const getStoreRejectionReason = () =>
      user?.role === 'seller' ? user?.store?.rejectionReason || null : null;

    return {
      user, isLoggedIn, loading, token,
      login, logout, getLogoutRedirectPath, updateUser, checkAuth,
      isSuperAdmin, isAdmin, isSeller,
      isStoreVerified, isStorePending, isStoreRejected, getStoreRejectionReason,
    };
  }, [user, isLoggedIn, loading, token, login, logout, getLogoutRedirectPath, updateUser, checkAuth]);

  return (
    <AuthContext.Provider
      value={value}
    >
      {children}
    </AuthContext.Provider>
  );
};